<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError, setUnauthorizedHandler } from './lib/api';
  import { router } from './lib/router.svelte';
  import { app } from './lib/stores.svelte';
  import { connect, disconnect, onRemoved } from './lib/ws';
  import { forgetDevice, resumePush, startWorker } from './lib/push';
  import Activate from './components/Activate.svelte';
  import Login from './components/Login.svelte';
  import Shell from './components/Shell.svelte';

  let ready = $state(false);
  let fatal = $state('');

  /** With a session: the user, their conversations, and the socket. */
  async function start(): Promise<boolean> {
    try {
      app.me = await api.me();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return false;
      throw e;
    }
    await app.loadConversations();
    void app.loadUsers().catch(() => {});
    void app.loadTaskSummary().catch(() => {});
    connect();
    void resumePush(app.info?.vapid_public_key).catch(() => {});
    return true;
  }

  function toLogin() {
    disconnect();
    app.reset();
    if (router.route.name !== 'activate') router.go('/login', { replace: true });
  }

  async function loggedIn() {
    if (await start()) router.go('/', { replace: true });
  }

  async function logout() {
    // While the session still exists: the device is the account's.
    await forgetDevice();
    await api.logout().catch(() => {});
    toLogin();
  }

  onMount(async () => {
    setUnauthorizedHandler(toLogin);
    startWorker((url) => router.go(url));
    onRemoved((id) => {
      if (router.route.name === 'conversation' && router.route.id === id) router.go('/');
    });
    try {
      app.info = await api.info();
      if (router.route.name !== 'activate') {
        const session = await start();
        if (!session) toLogin();
        else if (router.route.name === 'login') router.go('/', { replace: true });
      }
    } catch (e) {
      fatal = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
    ready = true;
  });

  // « (3) Nom » : conversations with unread messages in the tab title.
  $effect(() => {
    const name = app.info?.app_name ?? 'Messagerie';
    document.title = app.unreadTotal > 0 ? `(${app.unreadTotal}) ${name}` : name;
  });
</script>

{#if fatal}
  <p class="fatal error" role="alert">{fatal}</p>
{:else if !ready}
  <p class="loading">Chargement…</p>
{:else if router.route.name === 'activate'}
  <Activate token={router.route.token} />
{:else if router.route.name === 'login' || !app.me}
  <Login onLoggedIn={loggedIn} />
{:else}
  <Shell onLogout={logout} />
{/if}

<style>
  .loading,
  .fatal {
    margin: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .loading {
    color: var(--muted);
  }
  .fatal {
    margin: 24px;
    height: auto;
  }
</style>
